import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import Collapsible from 'react-collapsible';
import cloneDeep from 'lodash.clonedeep';
import uniqBy from 'lodash.uniqby';
import isEmpty from 'lodash.isempty';
import isEqual from 'lodash.isequal';
import cn from 'classnames';

import { Button, Checkbox } from '../../../../components';

const DISCHARGE_ADT_TYPE = 'ADT_A03';

const MULTI_OPTION_OPTIONS = [
	'adt_types',
	// 'discharge_dispositions',
	'adt_class' /* 'facility_filter', 'provider_filter' */,
];

const InlineFilter = ({
	isLoading,
	disabled,
	filterOptions,
	currentFilter,
	onShowMoreFilters,
	onApply,
	onShowProviderFilter,
	onReset,
}) => {
	const [filterSelections, setFilterSelections] = useState({});
	const [origFilterSelections, setOrigFilterSelections] = useState({});
	const [allowDispositions, setAllowDispositions] = useState(false);

	const handleApply = () => {
		// const { filterSelections } = this.state;
		// keep only needed data
		// adt_types.adt_type
		// discharge_dispositions.code
		// adt_class.adt_class_type
		// facility_filter.sending_facility_oid
		// provider_filter.Provider_NPI
		onApply({
			...filterSelections,
			adt_types: filterSelections.adt_types.map((adtType) => ({
				adt_type: adtType.adt_type,
			})),
			// discharge_dispositions: filterSelections.discharge_dispositions.map(
			// 	(disposition) => ({ code: disposition.code })
			// ),
			adt_class: filterSelections.adt_class.map((adtClass) => ({
				adt_class_type: adtClass.adt_class_type,
			})),
			// facility_filter: filterSelections.facility_filter.map(facility => ({ sending_facility_oid: facility.sending_facility_oid })),
			// provider_filter: filterSelections.provider_filter.map(provider => ({ Provider_NPI: provider.Provider_NPI })),
			employers: filterSelections.employers.map((employer) => employer),
			locations: filterSelections.locations.map((location) => location),
			programs: filterSelections.programs.map((program) => program),
		});
	};

	useEffect(() => {
		if (!isLoading && !isEmpty(filterOptions) && currentFilter) {
			// set up local selections
			const filterSelections = {
				...filterOptions,
				...cloneDeep(currentFilter),
			};

			if (Array.isArray(filterSelections.provider_filter)) {
				const stringProviderIDs = filterSelections.provider_filter.map(
					({ Provider_NPI }) => ({ Provider_NPI: `${Provider_NPI}` })
				);
				filterSelections.provider_filter = uniqBy(
					stringProviderIDs,
					'Provider_NPI'
				);
			}

			// if "all", preselect all options
			Object.keys(filterSelections).forEach((key) => {
				if (
					['employers', 'locations', 'programs'].includes(key) &&
					filterSelections[key] === null
				) {
					filterSelections[key] = [];
				} else if (
					MULTI_OPTION_OPTIONS.includes(key) &&
					filterSelections[key] === null
				) {
					filterSelections[key] = [...filterOptions[key]];
				}
			});

			// handle discharge not selected
			const allowDispositions = Boolean(
				filterSelections.adt_types.find(
					(at) => at.adt_type === DISCHARGE_ADT_TYPE
				)
			);
			if (!allowDispositions) {
				filterSelections.discharge_dispositions = [];
			}

			setFilterSelections(filterSelections);
			setAllowDispositions(allowDispositions);
			setOrigFilterSelections(filterSelections);
		}
	}, [isLoading, filterOptions, currentFilter]);

	const isClean = () => isEqual(filterSelections, origFilterSelections);

	const handleValueToggle = ({ target }) => {
		setFilterSelections({
			...filterSelections,
			[target.name]: target.checked,
		});
	};

	// const handleClearAll = ({ target }) => {
	// 	// handle discharge/disposition
	// 	if (target.name === 'adt_types') {
	// 		setAllowDispositions(false);
	// 		setFilterSelections({
	// 			...filterSelections,
	// 			discharge_dispositions: [],
	// 			[target.name]: [],
	// 		});
	// 	} else {
	// 		setFilterSelections({
	// 			...filterSelections,
	// 			[target.name]: [],
	// 		});
	// 	}
	// };

	// const handleSelectAll = ({ target }) => {
	// 	// handle discharge/disposition
	// 	if (target.name === 'adt_types') {
	// 		setAllowDispositions(true);
	// 		setFilterSelections({
	// 			...filterSelections,
	// 			[target.name]: [...filterOptions[target.name]],
	// 		});
	// 	} else {
	// 		setFilterSelections({
	// 			...filterSelections,
	// 			[target.name]: [...filterOptions[target.name]],
	// 		});
	// 	}
	// };

	const handleArrayValueToggle = ({ target }) => {
		const [name, prop] = target.name.split('.');
		if (target.checked) {
			const currFilterSelections = cloneDeep(filterSelections);
			if (!currFilterSelections[name]) {
				currFilterSelections[name] = [];
			}
			// handle discharge/disposition
			if (name === 'adt_types' && target.value === DISCHARGE_ADT_TYPE) {
				setAllowDispositions(true);
			}
			setFilterSelections({
				...currFilterSelections,
				[name]: [...currFilterSelections[name], { [prop]: target.value }],
			});
		} else {
			// handle discharge/disposition
			if (name === 'adt_types' && target.value === DISCHARGE_ADT_TYPE) {
				setAllowDispositions(false);
				setFilterSelections({
					...filterSelections,
					discharge_dispositions: [],
					[name]: filterSelections[name].filter(
						(arrayVal) => arrayVal[prop] !== target.value
					),
				});
			} else {
				setFilterSelections({
					...filterSelections,
					[name]: filterSelections[name].filter(
						(arrayVal) => arrayVal[prop] !== target.value
					),
				});
			}
		}
	};

	const handleShowProviderFilter = (event) => {
		event.preventDefault();
		onShowProviderFilter();
	};

	const handleShowFacilitiesFilter = (event) => {
		event.preventDefault();
		onShowMoreFilters('facility_filter');
	};

	const handleShowPrimaryFilter = (event) => {
		event.preventDefault();
		onShowMoreFilters('primary_payers');
	};

	const handleShowSecondaryFilter = (event) => {
		event.preventDefault();
		onShowMoreFilters('secondary_payers');
	};

	const handleShowDispCodeFilter = (event) => {
		event.preventDefault();
		if (allowDispositions) {
			onShowMoreFilters('discharge_dispositions');
		}
	};

	let providersFiltered = 0,
		facilitiesFiltered = 0,
		primariesFiltered = 0,
		secondariesFiltered = 0;
	if (Array.isArray(currentFilter.provider_filter)) {
		providersFiltered = currentFilter.provider_filter.length;
	} else if (Array.isArray(filterSelections.provider_filter)) {
		providersFiltered = filterSelections.provider_filter.length;
	}
	if (Array.isArray(currentFilter.facility_filter)) {
		facilitiesFiltered = currentFilter.facility_filter.length;
	} else if (Array.isArray(filterSelections.facility_filter)) {
		facilitiesFiltered = filterSelections.facility_filter.length;
	}
	if (currentFilter.primary_payers) {
		primariesFiltered = Object.keys(currentFilter.primary_payers).length;
	} else if (filterSelections.primary_payers) {
		primariesFiltered = Object.keys(filterSelections.primary_payers).length;
	}
	if (currentFilter.secondary_payers) {
		secondariesFiltered = Object.keys(currentFilter.secondary_payers).length;
	} else if (filterSelections.secondary_payers) {
		secondariesFiltered = Object.keys(filterSelections.secondary_payers).length;
	}

	return (
		<div className="panel panel-primary">
			<div className="panel-heading">
				<div className="pull-right">
					<Button
						disabled={isLoading}
						title="Reset"
						onClick={onReset}
						small
						className="mr-sm"
						info
					/>
					<Button
						disabled={disabled || isClean()}
						title="Apply"
						onClick={handleApply}
						small
					/>
				</div>
				Filter Options
			</div>
			<div className="panel-body">
				<Collapsible trigger="Record Filters" open>
					<div>
						<Checkbox
							checked={filterSelections.display_unread_notifications}
							onChange={handleValueToggle}
							label="Exclude Archived ADTs"
							name="display_unread_notifications"
						/>
					</div>
					<div>
						<Checkbox
							checked={filterSelections.starred_message}
							onChange={handleValueToggle}
							label="Starred ADTs Only"
							name="starred_message"
						/>
					</div>
				</Collapsible>
				<Collapsible
					trigger={`ADT Types ${
						Array.isArray(filterSelections.adt_types)
							? `(${filterSelections.adt_types.length})`
							: ''
					}`}
				>
					{filterOptions &&
						filterOptions.adt_types &&
						filterOptions.adt_types.map((adtType) => (
							<div key={adtType.adt_type}>
								<Checkbox
									checked={
										filterSelections.adt_types &&
										Boolean(
											filterSelections.adt_types.find(
												(at) => at.adt_type === adtType.adt_type
											)
										)
									}
									onChange={handleArrayValueToggle}
									label={adtType.name}
									name="adt_types.adt_type"
									value={adtType.adt_type}
								/>
							</div>
						))}
				</Collapsible>
				{/* <Collapsible
					trigger={`Disposition Codes ${
						Array.isArray(filterSelections.discharge_dispositions)
							? `(${filterSelections.discharge_dispositions.length})`
							: ''
					}`}
				>
					<div>
						<Button
							title="Clear All"
							onClick={handleClearAll}
							info
							small
							name="discharge_dispositions"
						/>{' '}
						<Button
							title="Select All"
							onClick={handleSelectAll}
							info
							small
							name="discharge_dispositions"
						/>
					</div>
					{filterOptions &&
						filterOptions.discharge_dispositions &&
						filterOptions.discharge_dispositions.map((disposition) => (
							<div key={disposition.code}>
								<Checkbox
									checked={
										filterSelections.discharge_dispositions &&
										Boolean(
											filterSelections.discharge_dispositions.find(
												(d) => d.code === disposition.code
											)
										)
									}
									onChange={handleArrayValueToggle}
									label={`${disposition.code} - ${disposition.description}`}
									name="discharge_dispositions.code"
									value={disposition.code}
									disabled={!allowDispositions}
								/>
							</div>
						))}
				</Collapsible> */}
				<div className="show-more-wrapper discharge-disp-filter">
					<a
						href="#"
						className={cn('btn', 'btn-link', {
							disabled: isLoading || !allowDispositions,
						})}
						onClick={handleShowDispCodeFilter}
					>
						Disposition Codes{' '}
						{Array.isArray(filterSelections.discharge_dispositions) &&
						filterSelections.discharge_dispositions.length > 0
							? `(${filterSelections.discharge_dispositions.length})`
							: ''}
					</a>
				</div>
				<Collapsible
					trigger={`ADT Classes ${
						Array.isArray(filterSelections.adt_class)
							? `(${filterSelections.adt_class.length})`
							: ''
					}`}
				>
					{filterOptions &&
						filterOptions.adt_class &&
						filterOptions.adt_class.map((cl) => (
							<div key={cl.adt_class_type}>
								<Checkbox
									checked={
										filterSelections.adt_class &&
										Boolean(
											filterSelections.adt_class.find(
												(ac) => ac.adt_class_type === cl.adt_class_type
											)
										)
									}
									onChange={handleArrayValueToggle}
									label={cl.adt_class_description}
									name="adt_class.adt_class_type"
									value={cl.adt_class_type}
								/>
							</div>
						))}
				</Collapsible>
				<Collapsible
					triggerClassName={cn({
						'has-selections':
							Array.isArray(filterSelections.employers) &&
							filterSelections.employers.length > 0,
					})}
					trigger={`Employers ${
						Array.isArray(filterSelections.employers)
							? `(${filterSelections.employers.length})`
							: ''
					}`}
				>
					{filterOptions &&
						filterOptions.employers &&
						filterOptions.employers.map(({ employer }) => (
							<div key={employer}>
								<Checkbox
									checked={
										filterSelections.employers &&
										Boolean(
											filterSelections.employers.find(
												(e) => e.employer === employer
											)
										)
									}
									onChange={handleArrayValueToggle}
									label={employer}
									name="employers.employer"
									value={employer}
								/>
							</div>
						))}
				</Collapsible>
				<Collapsible
					triggerClassName={cn({
						'has-selections':
							Array.isArray(filterSelections.locations) &&
							filterSelections.locations.length > 0,
					})}
					trigger={`Locations ${
						Array.isArray(filterSelections.locations)
							? `(${filterSelections.locations.length})`
							: ''
					}`}
				>
					{filterOptions &&
						filterOptions.locations &&
						filterOptions.locations.map(({ location }) => (
							<div key={location}>
								<Checkbox
									checked={
										filterSelections.locations &&
										Boolean(
											filterSelections.locations.find(
												(l) => l.location === location
											)
										)
									}
									onChange={handleArrayValueToggle}
									label={location}
									name="locations.location"
									value={location}
								/>
							</div>
						))}
				</Collapsible>
				<Collapsible
					triggerClassName={cn({
						'has-selections':
							Array.isArray(filterSelections.programs) &&
							filterSelections.programs.length > 0,
					})}
					trigger={`Special Programs ${
						Array.isArray(filterSelections.programs)
							? `(${filterSelections.programs.length})`
							: ''
					}`}
				>
					{filterOptions &&
						filterOptions.programs &&
						filterOptions.programs.map(({ program }) => (
							<div key={program}>
								<Checkbox
									checked={
										filterSelections.programs &&
										Boolean(
											filterSelections.programs.find(
												(p) => p.program === program
											)
										)
									}
									onChange={handleArrayValueToggle}
									label={program}
									name="programs.program"
									value={program}
								/>
							</div>
						))}
				</Collapsible>
				<div className="show-more-wrapper provider-filter">
					<a
						href="#"
						className={cn('btn', 'btn-link', { disabled: isLoading })}
						onClick={handleShowProviderFilter}
					>
						Providers {providersFiltered > 0 ? `(${providersFiltered})` : ''}
					</a>
				</div>
				<div className="show-more-wrapper facilities-filter">
					<a
						href="#"
						className={cn('btn', 'btn-link', { disabled: isLoading })}
						onClick={handleShowFacilitiesFilter}
					>
						Facilities {facilitiesFiltered > 0 ? `(${facilitiesFiltered})` : ''}
					</a>
				</div>
				<div className="show-more-wrapper primary-filter">
					<a
						href="#"
						className={cn('btn', 'btn-link', { disabled: isLoading })}
						onClick={handleShowPrimaryFilter}
					>
						Primary Payers/Plans{' '}
						{primariesFiltered > 0 ? `(${primariesFiltered})` : ''}
					</a>
				</div>
				<div className="show-more-wrapper secondary-filter">
					<a
						href="#"
						className={cn('btn', 'btn-link', { disabled: isLoading })}
						onClick={handleShowSecondaryFilter}
					>
						Secondary Payers/Plans{' '}
						{secondariesFiltered > 0 ? `(${secondariesFiltered})` : ''}
					</a>
				</div>
			</div>
		</div>
	);
};

InlineFilter.propTypes = {
	isLoading: PropTypes.bool,
	disabled: PropTypes.bool,
	filterOptions: PropTypes.object,
	currentFilter: PropTypes.object,
	onShowMoreFilters: PropTypes.func,
	onApply: PropTypes.func,
	onShowProviderFilter: PropTypes.func,
	onReset: PropTypes.func,
	onShowFacilitiesFilter: PropTypes.func,
};

export default InlineFilter;
